import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import useAuth from '../../components/hooks/useAuth';
import { message, Spin } from 'antd';
import { useUserRole } from "../../components/context/UserRoleContext";
import ForceMfaModal from "../../components/mfa/ForceMfaModal";

function Login({ title }) {
  const { setUserRole } = useUserRole();

  // ── Credential step state (existing) ──────────────────────────────────────
  const [loginInput, setLogin] = useState({
    email: "",
    password: "",
    errors: [],
    loading: false,
  });

  // ── Forced MFA setup state ────────────────────────────────────────────────
  const [showForceMfa, setShowForceMfa] = useState(false);

  // ── MFA step state ─────────────────────────────────────────────────────────
  // step: 'credentials' | 'otp'
  const [step, setStep] = useState('credentials');
  const [mfaToken, setMfaToken] = useState(null); // short-lived token from login
  const [mfaMode, setMfaMode] = useState('totp'); // 'totp' | 'backup'
  const [otpInput, setOtpInput] = useState({
    otp: "",
    errors: [],
    loading: false,
  });

  const handleInput = (e) => {
    e.persist();
    const { name, value } = e.target;
    setLogin({ ...loginInput, [name]: value });
  };

  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    if (auth) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          if (u.mfa_enabled === false) {
            setShowForceMfa(true);
            return;
          }
        } catch (e) {}
      }
      navigate('/dashboard');
    }
  }, [auth, navigate]);

  // ── Step 1: Credential submit (unchanged business logic) ───────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    const data = {
      email: loginInput.email,
      password: loginInput.password,
    };
    setLogin((prev) => ({ ...prev, loading: true }));
    try {
      const response = await apiService.post('auth/login', data);
      if (response.status === 200) {

        // ── MFA required: switch to OTP step ──────────────────────────────
        if (response.data.mfa_required) {
          setMfaToken(response.data.mfa_token);
          setStep('otp');
          setMfaMode('totp');
          setOtpInput({ otp: '', errors: [], loading: false });
          setLogin((prev) => ({ ...prev, loading: false, errors: [] }));
          return;
        }

        // ── Credentials verified ───────────────────────────────────────────
        const { id, first_name, last_name, email, phone, roles, mfa_enabled } = response.data.data;
        localStorage.setItem('user', JSON.stringify({ id, first_name, last_name, email, phone, roles, mfa_enabled: Boolean(mfa_enabled) }));
        localStorage.setItem('token', response.data.access_token);
        setUserRole(roles[0]);

        // If non-MFA user, force MFA setup before entering dashboard
        if (!mfa_enabled) {
          setShowForceMfa(true);
          setLogin((prev) => ({ ...prev, loading: false, errors: [] }));
          return;
        }

        navigate('/dashboard');
        setLogin((prev) => ({ ...prev, loading: false, errors: [] }));
        message.success(response.data.message);
      }
    } catch (error) {
      setLogin((prev) => ({ ...prev, loading: false }));
      if (error.response) {
        if (error.response.status === 422) {
          setLogin({ ...loginInput, errors: error.response.data.errors });
        } else if (error.response.status === 500) {
          setLogin({ ...loginInput, errors: [] });
          message.error(error.response.data.message);
        } else {
          message.error('Something went wrong. Please try again later.');
        }
      } else {
        message.error('Some Problem Occured! Please try again later.');
      }
    }
  };

  // ── Forced MFA Handlers ───────────────────────────────────────────────────
  const handleForceMfaComplete = () => {
    setShowForceMfa(false);
    navigate('/dashboard');
    message.success("Two-Factor Authentication configured. Welcome to Dashboard!");
  };

  const handleForceMfaLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUserRole(null);
    setShowForceMfa(false);
    setLogin({ email: "", password: "", errors: [], loading: false });
  };

  // ── Step 2: MFA verification (TOTP or Backup Code) ─────────────────────────
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const code = otpInput.otp.trim().toUpperCase();

    if (mfaMode === 'totp' && code.length !== 6) {
      setOtpInput((prev) => ({
        ...prev,
        errors: { otp: { message: 'Please enter the 6-digit code.' } },
      }));
      return;
    }

    if (mfaMode === 'backup' && !/^[A-Z0-9]{5}-[A-Z0-9]{5}$/.test(code)) {
      setOtpInput((prev) => ({
        ...prev,
        errors: { otp: { message: 'Please enter a valid backup code (format: XXXXX-XXXXX).' } },
      }));
      return;
    }

    setOtpInput((prev) => ({ ...prev, loading: true, errors: [] }));
    try {
      const response = await apiService.post('auth/mfa/login', {
        otp: code,
        mfa_token: mfaToken,
      });
      if (response.status === 200) {
        const { id, first_name, last_name, email, phone, roles } = response.data.data;
        localStorage.setItem('user', JSON.stringify({ id, first_name, last_name, email, phone, roles }));
        localStorage.setItem('token', response.data.access_token);
        setUserRole(roles[0]);
        navigate('/dashboard');
        message.success(response.data.message);
      }
    } catch (error) {
      setOtpInput((prev) => ({ ...prev, loading: false }));
      if (error.response) {
        if (error.response.status === 422) {
          setOtpInput((prev) => ({ ...prev, errors: error.response.data.errors }));
        } else if (error.response.status === 401) {
          message.error(error.response.data.message || 'Session expired. Please login again.');
          setStep('credentials');
          setMfaToken(null);
          setMfaMode('totp');
          setOtpInput({ otp: '', errors: [], loading: false });
        } else {
          message.error(error.response.data.message || 'Something went wrong. Please try again.');
        }
      } else {
        message.error('Some Problem Occured! Please try again later.');
      }
    }
  };

  const handleBackupCodeInput = (e) => {
    let raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
    if (raw.length > 5) {
      raw = `${raw.slice(0, 5)}-${raw.slice(5)}`;
    }
    setOtpInput((prev) => ({ ...prev, otp: raw, errors: [] }));
  };

  return (
    <div className="bg-gradient-primary">
      <div className="container">
        <div className="row justify-content-center align-items-center">
          <div className="col-xl-10 col-lg-12 col-md-9">
            <div className="card o-hidden border-0 shadow-lg my-5">
              <div className="card-body p-0">
                <div className="row">
                  <div className="col-lg-6 d-flex justify-content-center align-items-center bg-login-image">
                    <div className="logo p-5 w-100">
                      <img src="img/logo.gif" alt="" className="img-fluid" />
                    </div>
                  </div>

                  <div className="col-lg-6">
                    {/* ── Step 1: Email + Password (existing UI — unchanged) ── */}
                    {step === 'credentials' && (
                      <Spin spinning={loginInput.loading}>
                        <div className="p-5">
                          <div className="text-center">
                            <h1 className="h4 text-gray-900 mb-4">{title}</h1>
                          </div>
                          <form className="user" onSubmit={handleLogin}>
                            <div className="form-group">
                              <input
                                type="email"
                                className={`form-control form-control-user ${loginInput.errors?.email ? 'is-invalid' : ''}`}
                                id="exampleInputEmail"
                                aria-describedby="emailHelp"
                                placeholder="Email"
                                name="email"
                                value={loginInput.email}
                                onChange={handleInput}
                              />
                              <span className="text-danger">{loginInput.errors.email?.message}</span>
                            </div>
                            <div className="form-group">
                              <input
                                type="password"
                                className={`form-control form-control-user ${loginInput.errors?.password ? 'is-invalid' : ''}`}
                                id="exampleInputPassword"
                                placeholder="Password"
                                name="password"
                                value={loginInput.password}
                                onChange={handleInput}
                              />
                              <span className="text-danger">{loginInput.errors.password?.message}</span>
                            </div>
                            <div className="form-group">
                              <div className="custom-control custom-checkbox small">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id="customCheck"
                                />
                                <label className="custom-control-label" htmlFor="customCheck">
                                  Remember Me
                                </label>
                              </div>
                            </div>
                            <button type="submit" className="btn btn-primary btn-user btn-block">
                              Login
                            </button>
                            <hr />
                            <div className="text-center">
                              <Link className="small" href="">Forgot Password?</Link>
                            </div>
                          </form>
                        </div>
                      </Spin>
                    )}

                    {/* ── Step 2: MFA Verification (TOTP or Backup Code) ── */}
                    {step === 'otp' && (
                      <Spin spinning={otpInput.loading}>
                        <div className="p-5">
                          <div className="text-center mb-4">
                            <h1 className="h4 text-gray-900 mb-2">Two-Factor Authentication</h1>
                            <p className="text-muted small">
                              {mfaMode === 'totp'
                                ? 'Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.) and enter your 6-digit verification code.'
                                : 'Enter one of your saved emergency backup recovery codes.'}
                            </p>
                          </div>
                          <form className="user" onSubmit={handleOtpVerify}>
                            <div className="form-group">
                              {mfaMode === 'totp' ? (
                                <input
                                  type="text"
                                  className={`form-control form-control-user text-center ${otpInput.errors?.otp ? 'is-invalid' : ''}`}
                                  placeholder="000 000"
                                  name="otp"
                                  maxLength={6}
                                  value={otpInput.otp}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setOtpInput((prev) => ({ ...prev, otp: val, errors: [] }));
                                  }}
                                  autoFocus
                                  autoComplete="one-time-code"
                                  inputMode="numeric"
                                  style={{ letterSpacing: '0.4em', fontSize: '1.3rem' }}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className={`form-control form-control-user text-center ${otpInput.errors?.otp ? 'is-invalid' : ''}`}
                                  placeholder="XXXXX-XXXXX"
                                  name="otp"
                                  maxLength={11}
                                  value={otpInput.otp}
                                  onChange={handleBackupCodeInput}
                                  autoFocus
                                  autoComplete="off"
                                  style={{ letterSpacing: '0.2em', fontSize: '1.1rem', textTransform: 'uppercase' }}
                                />
                              )}
                              <span className="text-danger d-block text-center mt-1">{otpInput.errors?.otp?.message}</span>
                            </div>

                            <button
                              type="submit"
                              className="btn btn-primary btn-user btn-block"
                              disabled={mfaMode === 'totp' ? otpInput.otp.length !== 6 : otpInput.otp.length !== 11}
                            >
                              Verify & Sign In
                            </button>

                            <div className="text-center mt-3">
                              {mfaMode === 'totp' ? (
                                <button
                                  type="button"
                                  className="btn btn-link text-muted small p-0"
                                  onClick={() => {
                                    setMfaMode('backup');
                                    setOtpInput({ otp: '', errors: [], loading: false });
                                  }}
                                >
                                  Can't access your authenticator? Use a backup code
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-link text-muted small p-0"
                                  onClick={() => {
                                    setMfaMode('totp');
                                    setOtpInput({ otp: '', errors: [], loading: false });
                                  }}
                                >
                                  ← Use authenticator app code instead
                                </button>
                              )}
                            </div>

                            <hr />
                            <div className="text-center">
                              <button
                                type="button"
                                className="btn btn-link small p-0"
                                onClick={() => {
                                  setStep('credentials');
                                  setMfaToken(null);
                                  setMfaMode('totp');
                                  setOtpInput({ otp: '', errors: [], loading: false });
                                }}
                              >
                                ← Back to Login
                              </button>
                            </div>
                          </form>
                        </div>
                      </Spin>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ── Mandatory Forced MFA Setup Modal ── */}
      <ForceMfaModal
        open={showForceMfa}
        onComplete={handleForceMfaComplete}
        onLogout={handleForceMfaLogout}
      />
    </div>
  );
}

export default Login;